import React, { useState, useRef } from 'react';

interface FileDropImportProps {
  onImport: (file: File) => void;
  isImporting: boolean;
  accept?: string;
  maxSizeMB?: number;
  className?: string;
  showIcon?: boolean;
  compact?: boolean;
  theme?: 'light' | 'dark';
}

const FileDropImport: React.FC<FileDropImportProps> = ({
  onImport,
  isImporting,
  accept = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.json',
  maxSizeMB = 10,
  className = '',
  showIcon = true,
  compact = false,
  theme = 'light',
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Calculate size in user-friendly format
  const formatSize = (sizeInMB: number): string => {
    return sizeInMB >= 1 ? `${sizeInMB}MB` : `${sizeInMB * 1024}KB`;
  };
  
  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  // Get file type description
  const getFileTypeDescription = (acceptString: string): string => {
    const types = acceptString.split(',').map(t => t.trim());
    
    // Map file extensions to friendly names
    const typeMap: Record<string, string> = {
      '.pdf': 'PDF',
      '.doc': 'Word',
      '.docx': 'Word',
      '.xls': 'Excel',
      '.xlsx': 'Excel',
      '.csv': 'CSV',
      '.json': 'JSON',
      '.txt': 'Text',
    };
    
    const friendlyNames = types.map(type => typeMap[type] || type.replace('.', '').toUpperCase());
    
    // Remove duplicates using Object.keys with object
    const uniqueNamesObj: Record<string, boolean> = {};
    friendlyNames.forEach(name => {
      uniqueNamesObj[name] = true;
    });
    const uniqueNames = Object.keys(uniqueNamesObj);
    
    return uniqueNames.join(', ');
  };
  
  const validateFile = (file: File): boolean => {
    // Reset previous errors
    setErrorMessage(null);
    
    // Check file size
    if (file.size > maxSizeBytes) {
      setErrorMessage(`File is too large. Maximum size is ${formatSize(maxSizeMB)}.`);
      return false;
    }
    
    // Check file type if accept is specified
    if (accept && accept !== '*') {
      const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
      const acceptedTypes = accept.split(',').map(type => type.trim());
      
      const isAccepted = acceptedTypes.some(type => {
        // Handle MIME types
        if (type.includes('/')) {
          return file.type === type;
        }
        // Handle extensions like .pdf
        return fileExtension === type;
      });
      
      if (!isAccepted) {
        setErrorMessage(`Invalid file type. Allowed: ${getFileTypeDescription(accept)}`);
        return false;
      }
    }
    
    return true;
  };
  
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (isImporting) return;
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        onImport(file);
      }
    }
  };
  
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!isImporting) {
      setIsDragging(true);
    }
  };
  
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isImporting) return;
    
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        onImport(file);
      }
      
      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const handleClick = () => {
    if (!isImporting && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Determine theme-based colors
  const getThemeClasses = () => {
    switch (theme) {
      case 'dark':
        return {
          border: isDragging ? 'border-[#9d7ae0]' : 'border-slate-600',
          bg: isDragging ? 'bg-[#2e245a]' : 'bg-slate-800',
          text: 'text-white',
          subtext: 'text-slate-300',
          button: 'bg-slate-700 border-slate-500 text-white hover:bg-slate-600',
          icon: isDragging ? 'text-[#9d7ae0]' : 'text-slate-300',
        };
      case 'light':
      default:
        return {
          border: isDragging ? 'border-[#6741c6]' : 'border-slate-300',
          bg: isDragging ? 'bg-[#f8f5ff]' : 'bg-white',
          text: 'text-slate-800',
          subtext: 'text-slate-500',
          button: 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50',
          icon: isDragging ? 'text-[#6741c6]' : 'text-slate-400',
        };
    }
  };
  
  const themeClasses = getThemeClasses();
  
  return (
    <div className={`relative ${className}`}>
      <div
        className={`border-2 border-dashed ${themeClasses.border} rounded-lg ${
          compact ? 'p-4' : 'p-6'
        } transition-all duration-300 ${themeClasses.bg} ${
          isImporting 
            ? 'opacity-70 cursor-not-allowed' 
            : 'cursor-pointer hover:shadow-lg hover:scale-[1.01] hover:border-[#6741c6] active:scale-[0.99]'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
        data-testid="file-drop-area"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
          disabled={isImporting}
          data-testid="file-input"
        />
        
        <div className="flex flex-col items-center justify-center text-center">
          {showIcon && (
            <div className={compact ? "mb-2" : "mb-3"}>
              <svg 
                className={`${compact ? 'w-8 h-8' : 'w-12 h-12'} ${themeClasses.icon} 
                transition-all duration-300 ${isDragging ? 'transform -translate-y-1 scale-110 text-[#6741c6]' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={isDragging ? "2.5" : "2"}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  className={`${isDragging ? 'animate-pulse' : ''}`}
                />
              </svg>
            </div>
          )}
          
          {isImporting ? (
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#6741c6] mb-2"></div>
              <p className={`text-sm ${themeClasses.text}`}>
                <span className="sr-only">Importing</span>
                Extracting data...
              </p>
            </div>
          ) : (
            <>
              <p className={`text-sm font-medium ${themeClasses.text} mb-1 transition-all duration-200 ${isDragging ? 'text-[#6741c6] transform scale-110' : ''}`}>
                {isDragging ? 'Release to Upload' : compact ? 'Upload document' : 'Drop document here'}
              </p>
              <p className={`text-xs ${themeClasses.subtext} transition-opacity duration-200 ${isDragging ? 'opacity-80' : ''}`}>
                {getFileTypeDescription(accept)} {!compact && `(max. ${formatSize(maxSizeMB)})`}
              </p>
              <button
                type="button"
                className={`mt-3 px-4 py-2 border rounded-md text-sm font-medium ${themeClasses.button} 
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#6741c6] 
                transition-all duration-200 transform hover:scale-[1.03] active:scale-[0.98] 
                hover:shadow-md hover:border-[#6741c6] hover:text-[#6741c6]`}
                aria-label="Browse files"
                data-testid="browse-button"
              >
                {isDragging ? 'Drop it!' : compact ? 'Browse' : 'Select File'}
              </button>
              {errorMessage && (
                <p className="mt-2 text-xs text-red-500" role="alert">{errorMessage}</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileDropImport;